import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { initCSS2DRenderer, createSimpleGeometryCenterLabels2DObjects, disposeGroupLinesAndLabels, GeometryLabelData, Label2DInfo, updateLabel2DScales } from 'src/app/digital-twin/graphics/labels-utils';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import * as THREE from 'three';

@Injectable({
  providedIn: 'root'
})
export class TypicalDetailsLabelsService {

  // State management
  private _isActive = new BehaviorSubject<boolean>(false);
  public isActive$ = this._isActive.asObservable();

  // Labels 3D management
  private labelRenderer: CSS2DRenderer;
  private labelsGroup: THREE.Group;
  private currentLabelInfos: Label2DInfo[] = [];
  private currentAssetManager: AssetManager | null = null;

  constructor() { }

  /**
   * Activates typical details labels for brackets with TypicalDetail property
   */
  public activateTypicalDetailsLabels(assetManager: AssetManager): void {
    if (!assetManager?.asset || !assetManager?.graphics) {
      console.warn('AssetManager or graphics not available');
      return;
    }

    this.currentAssetManager = assetManager;
    this._isActive.next(true);
    this.initializeLabelsSystem();
    this.showTypicalDetailsLabels();

    console.log('🏷️ Typical details labels system activated');
  }

  /**
   * Deactivates typical details labels
   */
  public deactivateTypicalDetailsLabels(assetManager?: AssetManager): void {
    this.clearCurrentLabels();
    this._isActive.next(false);
    this.currentAssetManager = null;

    // Remove labels group from scene
    if (this.labelsGroup && assetManager?.graphics?.scene) {
      assetManager.graphics.scene.remove(this.labelsGroup as any);
      this.labelsGroup = null;
    }

    console.log('🏷️ Typical details labels system deactivated');
  }

  /**
   * Shows labels for brackets with TypicalDetail property
   */
  private showTypicalDetailsLabels(): void {
    if (!this._isActive.value || !this.currentAssetManager) {
      console.warn('Service not active or asset manager not available');
      return;
    }

    this.initializeLabelsSystem();
    this.clearCurrentLabels();

    const geometryLabels = this.extractTypicalDetailsLabelData();

    if (geometryLabels.length > 0) {
      this.currentLabelInfos = createSimpleGeometryCenterLabels2DObjects(
        this.labelsGroup,
        geometryLabels,
        this.currentAssetManager.graphics.camera as any
      );

      console.log(`🏷️ Displaying ${geometryLabels.length} typical details labels`);
    } else {
      console.warn('No brackets with typical details found to display labels');
    }

    this.requestSceneRender();
  }

  /**
   * Refreshes the labels
   */
  public refreshTypicalDetailsLabels(assetManager?: AssetManager): void {
    const manager = assetManager || this.currentAssetManager;

    if (!this._isActive.value || !manager) {
      console.warn('Cannot refresh labels - service not active or missing data');
      return;
    }

    console.log('🔄 Refreshing typical details labels');
    this.currentAssetManager = manager;
    this.showTypicalDetailsLabels();
  }

  /**
   * Checks if the service is active
   */
  public get isActive(): boolean {
    return this._isActive.value;
  }

  /**
   * Clears all current labels
   */
  public clearLabels(): void {
    this.clearCurrentLabels();
  }

  /**
   * Updates the scale of labels based on camera distance
   */
  public updateLabelScales(): void {
    if (this._isActive.value && this.currentLabelInfos.length > 0 && this.currentAssetManager) {
      updateLabel2DScales(this.currentLabelInfos, this.currentAssetManager.graphics.camera as any);
    }
  }

  /**
   * Dispose all resources
   */
  public dispose(): void {
    this.deactivateTypicalDetailsLabels();

    if (this.labelRenderer) {
      this.labelRenderer = null;
    }
  }

  // Private methods

  private initializeLabelsSystem(): void {
    if (!this.currentAssetManager) {
      return;
    }

    const container = document.getElementById('ThreeJS');
    if (!this.labelRenderer && container) {
      this.labelRenderer = initCSS2DRenderer(container);
    }

    if (!this.labelsGroup) {
      this.labelsGroup = new THREE.Group();
      this.labelsGroup.name = 'TypicalDetailsLabelsGroup';
      this.currentAssetManager.graphics.scene.add(this.labelsGroup as any);
    }
  }

  private clearCurrentLabels(): void {
    if (this.labelsGroup) {
      disposeGroupLinesAndLabels(this.labelsGroup);
      this.currentLabelInfos = [];
      this.requestSceneRender();
    }
  }

  // Graphics only renders on request. clearCurrentLabels() drops the old label <div>s from the DOM
  // at once, while new CSS2DObjects only enter the DOM on the next labelRenderer.render(); without a
  // render request the labels vanish until the ~1 s safety render (they "blink").
  private requestSceneRender(): void {
    this.currentAssetManager?.graphics?.requestRender();
  }

  private extractTypicalDetailsLabelData(): GeometryLabelData[] {
    const geometryLabels: GeometryLabelData[] = [];

    if (!this.currentAssetManager?.asset) {
      return geometryLabels;
    }

    try {
      const visibleRegions = this.currentAssetManager.asset.GetStructureVisibleRegions();

      for (const region of visibleRegions) {
        for (const section of region.sections) {
          if (!section.visible) continue;

          // Get brackets with TypicalDetail property
          if (section.linearElements) {
            // Track which FamilyGuids we've already added labels for to avoid duplicates
            const labeledFamilyGuids: string[] = [];

            for (const element of section.linearElements) {
              if (element.TypeId !== ELEMENTSTYPESIDS.BRACKET) continue;

              const typicalDetail = element.properties?.TypicalDetail;
              if (!typicalDetail?.FamilyId) continue;

              // Only show one label per family (use the family model if available, or first bracket)
              if (labeledFamilyGuids.includes(typicalDetail.FamilyGuid)) {
                continue;
              }

              // Prefer the family model bracket for the label position
              if (!typicalDetail.IsFamilyModel) {
                // Check if there's a family model in this section
                const familyModel = section.linearElements.find(
                  el => el.properties?.TypicalDetail?.FamilyGuid === typicalDetail.FamilyGuid &&
                        el.properties?.TypicalDetail?.IsFamilyModel === true
                );
                if (familyModel) {
                  // Skip this one, we'll process the family model instead
                  continue;
                }
              }

              labeledFamilyGuids.push(typicalDetail.FamilyGuid);

              const centerPosition = this.getElementGeometryCenter(element);
              if (centerPosition) {
                geometryLabels.push({
                  position: centerPosition,
                  label: typicalDetail.FamilyId
                });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting typical details label data:', error);
    }

    return geometryLabels;
  }

  private getElementGeometryCenter(element: any): THREE.Vector3 | null {
    try {
      // Strategy 1: Try to get bounding box from the element's 3D object
      const center = this.tryGetBoundingBoxCenter(element);
      if (center && this.isValidPosition(center)) {
        return center;
      }

      // Strategy 2: Try to calculate center from vertex positions
      const vertexCenter = this.tryGetVertexCenter(element);
      if (vertexCenter && this.isValidPosition(vertexCenter)) {
        return vertexCenter;
      }

      // Strategy 3: Try alternative geometry access paths
      const altCenter = this.tryAlternativeGeometryCenter(element);
      if (altCenter && this.isValidPosition(altCenter)) {
        return altCenter;
      }

      console.warn('Could not determine valid geometry center for element:', element.Name || 'Unnamed');
      return null;
    } catch (error) {
      console.error('Error getting element geometry center:', error);
      return null;
    }
  }

  private tryGetBoundingBoxCenter(element: any): THREE.Vector3 | null {
    try {
      const geometryObjects = [
        element.SurfaceDesign,
        element.geometry,
        element.mesh,
        element.object3d,
        element.threeObject
      ];

      for (const obj of geometryObjects) {
        if (obj) {
          try {
            const boundingBox = new THREE.Box3();
            boundingBox.setFromObject(obj);

            if (boundingBox.isEmpty() ||
                !isFinite(boundingBox.min.x) || !isFinite(boundingBox.min.y) || !isFinite(boundingBox.min.z) ||
                !isFinite(boundingBox.max.x) || !isFinite(boundingBox.max.y) || !isFinite(boundingBox.max.z)) {
              continue;
            }

            const center = new THREE.Vector3();
            boundingBox.getCenter(center);

            return center;
          } catch (e) {
            continue;
          }
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  private tryGetVertexCenter(element: any): THREE.Vector3 | null {
    try {
      let positions: number[] = [];

      const positionSources = [
        element.SurfaceDesign?.geometry?.attributes?.position?.array,
        element.geometry?.attributes?.position?.array,
        element.vertices,
        element.points,
        element.coordinates
      ];

      for (const source of positionSources) {
        if (source && Array.isArray(source) && source.length >= 3) {
          positions = source;
          break;
        }
      }

      if (positions.length < 3) {
        return null;
      }

      let sumX = 0, sumY = 0, sumZ = 0;
      const vertexCount = Math.floor(positions.length / 3);

      for (let i = 0; i < vertexCount; i++) {
        sumX += positions[i * 3];
        sumY += positions[i * 3 + 1];
        sumZ += positions[i * 3 + 2];
      }

      return new THREE.Vector3(
        sumX / vertexCount,
        sumY / vertexCount,
        sumZ / vertexCount
      );
    } catch (error) {
      return null;
    }
  }

  private tryAlternativeGeometryCenter(element: any): THREE.Vector3 | null {
    try {
      if (element.position) {
        if (element.position.x !== undefined && element.position.y !== undefined && element.position.z !== undefined) {
          return new THREE.Vector3(element.position.x, element.position.y, element.position.z);
        }
      }

      if (element.center) {
        if (element.center.x !== undefined && element.center.y !== undefined && element.center.z !== undefined) {
          return new THREE.Vector3(element.center.x, element.center.y, element.center.z);
        }
      }

      if (element.properties?.centroid) {
        const centroid = element.properties.centroid;
        if (centroid.x !== undefined && centroid.y !== undefined && centroid.z !== undefined) {
          return new THREE.Vector3(centroid.x, centroid.y, centroid.z);
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  private isValidPosition(position: THREE.Vector3): boolean {
    if (isFinite(position.x) && isFinite(position.y) && isFinite(position.z) &&
        !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z)) {
      return true;
    }
    return false;
  }
}
